import { getFingerprintDeviceId } from './fingerprint';
import { clearAnonymousUserId, getAnonymousUserId, setAnonymousUserId } from './storage';

export interface AnonymousSessionOptions {
  createAnonymousUser: () => Promise<string | null | undefined>;
  onError?: (error: unknown) => void;
}

export interface AnonymousSessionController {
  clearPromise: () => void;
  ensureAnonymousUser: () => Promise<string | null>;
}

export interface AnonymousUserSessionOptions {
  createAnonymousUserByDeviceId: (deviceId: string) => Promise<string | null | undefined>;
  getDeviceId?: () => Promise<string>;
  onError?: (error: unknown) => void;
}

export interface AnonymousUserSession {
  clearAnonymousUser: () => void;
  initAnonymousUser: () => Promise<string | null>;
}

export function createAnonymousSessionController(options: AnonymousSessionOptions): AnonymousSessionController {
  let anonymousUserPromise: Promise<string | null> | null = null;

  function clearPromise() {
    anonymousUserPromise = null;
  }

  async function ensureAnonymousUser(): Promise<string | null> {
    const cachedUserId = getAnonymousUserId();
    if (cachedUserId) {
      return cachedUserId;
    }

    if (!anonymousUserPromise) {
      anonymousUserPromise = options.createAnonymousUser()
        .then(userId => {
          if (!userId) {
            clearPromise();
            return null;
          }

          setAnonymousUserId(userId);
          return userId;
        })
        .catch(error => {
          clearPromise();
          options.onError?.(error);
          return null;
        });
    }

    return anonymousUserPromise;
  }

  return {
    clearPromise,
    ensureAnonymousUser,
  };
}

/**
 * 创建匿名用户会话管理器。
 * common 只负责浏览器指纹、storage 和并发去重。
 */
export function createAnonymousUserSession(options: AnonymousUserSessionOptions): AnonymousUserSession {
  const session = createAnonymousSessionController({
    createAnonymousUser: async () => {
      const deviceId = await (options.getDeviceId || getFingerprintDeviceId)();
      return options.createAnonymousUserByDeviceId(deviceId);
    },
    onError: options.onError,
  });

  return {
    clearAnonymousUser() {
      session.clearPromise();
      clearAnonymousUserId();
    },
    initAnonymousUser() {
      // 匿名用户创建失败不能阻断主流程，失败 promise 会被清理，下一次 init 会自动重试。
      return session.ensureAnonymousUser();
    },
  };
}
